import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import { ColorPickerDirective } from 'ngx-color-picker';

import { isLightColor, StandardColors } from '../../utils';

// The color shown when no color has been selected yet.
const DEFAULT_COLOR_HEX = '#ffffff';

// Marks the reset control this component adds to the picker dialog, so it is
// injected once per dialog rather than on every open.
const RESET_BUTTON_CLASS = 'capitan-color-picker-reset';


@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.css']
})
export class CapitanColorPickerComponent implements AfterViewInit, OnChanges {

  @Input()
  id: string;

  @Input()
  content: string;

  @Input()
  cpPosition = 'auto';

  /**
   * Render as a small square swatch instead of the full-width labelled input,
   * for use inline in a table row or beside a label (PRO-1369).
   */
  @Input()
  compact = false;

  /** Compact swatch size: `sm` is an inline square, `lg` a field-height rectangle. */
  @Input()
  size: 'sm' | 'lg' = 'sm';

  /** Accessible name for the compact swatch (the labelled input needs none). */
  @Input()
  ariaLabel = 'Choose a color';

  /**
   * The swatches offered above the picker, '#'-prefixed. Defaults to the
   * Capitan standard colors.
   */
  @Input()
  presetColors: string[] = StandardColors.map(sc => '#' + sc.colorHex);

  /**
   * Label for a reset control at the bottom of the picker dialog, e.g. 'Reset
   * to the default color'. Empty (the default) means no control; bind '' while
   * there is nothing to reset and the control disappears.
   */
  @Input()
  resetLabel = '';

  @Input()
  disabled = false;

  @Output()
  contentChange = new EventEmitter<string>();

  /** The reset control was clicked. The caller decides what "default" means. */
  @Output()
  resetSelected = new EventEmitter<void>();

  /** The picker dialog opened / closed, for callers with UI the dialog covers. */
  @Output()
  opened = new EventEmitter<void>();

  @Output()
  closed = new EventEmitter<void>();

  public colorHex: string;

  // So the reset control can close the dialog it sits in. A ViewChild query
  // reaches into whichever template branch rendered.
  @ViewChild(ColorPickerDirective)
  private picker: ColorPickerDirective;

  constructor(private elRef: ElementRef) {}

  ngAfterViewInit() {
    this.colorHex = this.getNormalizedColorHex(this.content);

    if (!this.content) {
      // Emit the displayed default so the bound value matches what the user
      // sees; otherwise submitting without opening the picker fails validation.
      // Deferred past the change detection pass that created this component.
      //
      // Callers for whom "no color" is a state worth keeping must bind the
      // color to SHOW rather than the stored override, so this never fires.
      Promise.resolve().then(() => this.handleColorChange());
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // `changes.content` is absent when only a sibling input changed.
    if (changes.content) {
      this.colorHex = this.getNormalizedColorHex(changes.content.currentValue);
    }
    if (changes.resetLabel) {
      // The reset control must follow the label while the dialog is OPEN:
      // picking a color is what gives a caller something to reset.
      const dialogEl = this.dialogElement();
      if (dialogEl) {
        this.syncResetButton(dialogEl);
      }
    }
  }

  /**
   * Return the given color as a '#'-prefixed hex value, falling back to the
   * default color if none was provided.
   */
  private getNormalizedColorHex(colorHex: string): string {
    if (!colorHex) {
      return DEFAULT_COLOR_HEX;
    }

    return colorHex.startsWith('#') ? colorHex : '#' + colorHex;
  }

  handleColorChange() {
    this.contentChange.emit(this.colorHex.substring(1));
  }

  getColorName() {
    for (const stdColor of StandardColors) {
      if ('#' + stdColor.colorHex === this.colorHex) {
        return stdColor.name;
      }
    }
    return 'Custom Color';
  }

  isLightColor() {
    return isLightColor(this.colorHex.substring(1));
  }

  handlePickerClose() {
    this.closed.emit();
  }

  handlePickerOpen() {
    this.opened.emit();
    const colorPickerEl = this.dialogElement();
    if (!colorPickerEl) {
      return;
    }
    this.syncResetButton(colorPickerEl);
    // ngx-color-picker positions the dialog in its own `setTimeout(…, 0)` after
    // emitting this event, so `style.top` is '' if read now; this timeout is
    // registered after the library's and so runs after it.
    setTimeout(() => this.nudgeDialogIntoView(colorPickerEl), 0);
  }

  /**
   * This component's OWN dialog, or null before the picker has built one.
   * Host-scoped: a document-wide `div.color-picker.open` lookup can return a
   * sibling picker's dialog when a page has several, since `open` is not yet
   * set on ours in the tick the open event fires.
   */
  private dialogElement(): HTMLElement | null {
    return this.elRef.nativeElement.querySelector('div.color-picker');
  }

  /** If the dialog would render above the top of the page, move it down. */
  private nudgeDialogIntoView(dialogEl: any) {
    const topInt = parseInt(dialogEl.style.top.split('px')[0].split('.')[0], 10);
    if (isNaN(topInt) || topInt >= 10) {
      return;
    }
    dialogEl.style.top = '10px';
    const arrowEl: any = dialogEl.querySelector('div.arrow');
    if (arrowEl) {
      // Absent unless the dialog is a popup — the arrow would point at nothing
      // now that the dialog has moved away from its trigger.
      arrowEl.style.display = 'none';
    }
  }

  /**
   * Add / update / remove the reset control inside the open dialog.
   * ngx-color-picker offers no way to project content into its dialog, and
   * scoped CSS can't reach it, hence a hand-built element with global
   * Bootstrap classes. `dialogEl` must be the host-scoped one (see
   * `dialogElement`).
   */
  private syncResetButton(dialogEl: HTMLElement) {
    const existing = dialogEl.querySelector('.' + RESET_BUTTON_CLASS);
    if (!this.resetLabel) {
      if (existing) {
        existing.remove();
      }
      return;
    }
    if (existing) {
      existing.textContent = this.resetLabel;
      return;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = RESET_BUTTON_CLASS + ' btn btn-link btn-sm btn-block';
    button.textContent = this.resetLabel;
    // Inside the dialog on purpose: `cpSaveClickOutside` treats a click anywhere
    // else as "accept the current color and close", which would fight the reset.
    button.addEventListener('click', () => this.handleReset());
    dialogEl.appendChild(button);
  }

  private handleReset() {
    if (this.picker) {
      this.picker.closeDialog();
    }
    this.resetSelected.emit();
  }
}
